import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";

const FACILITIES = ["Projector / Display", "Whiteboard / Flipchart", "Laptop/Computer", "Food & Refreshments", "Wi-Fi", "Stationery"];
const FALLBACK_LOCATIONS = ["Sector 62, Noida", "Sector 1, Noida"];

const fld = "w-full min-h-[46px] px-4 rounded-xl border border-slate-200 bg-slate-50 text-[15px] text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all";
const lbl = "block text-sm font-semibold text-slate-700 mb-1.5";

export default function VenueBookingForm({ path }) {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", start_date: "", pax: "", location: "", duration_days: "", remarks: "" });
  const [facilities, setFacilities] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(true);
  const [locations, setLocations] = useState(FALLBACK_LOCATIONS);

  useEffect(() => {
    api.get("/website/sections/centres")
      .then((r) => {
        const names = (((r.data || {}).items) || []).filter((c) => c && c.visible !== false && c.name).map((c) => c.name);
        if (names.length) setLocations(names);
      })
      .catch(() => {});
  }, []);

  const toggleFacility = (f) => setFacilities((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));

  const submit = async (e) => {
    e.preventDefault();
    const phone = form.phone.replace(/\D/g, "").slice(-10);
    if (!form.name.trim() || !form.company.trim() || !form.email.trim() || !form.start_date || !form.location || !form.pax || !form.duration_days) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (!validateConsent(consent)) return;
    setSending(true);
    try {
      await api.post("/venue-leads", {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone,
        email: form.email.trim(),
        start_date: form.start_date,
        pax: parseInt(form.pax, 10) || 1,
        location: form.location,
        duration_days: parseInt(form.duration_days, 10) || 1,
        facilities,
        remarks: form.remarks.trim(),
        attribution: { source: "venue_booking", path, page: path },
        consent_given: consent, consent_text: CONSENT_TEXT,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/80 p-10 text-center" data-testid="venue-form-success">
        <CheckCircle2 className="w-11 h-11 text-emerald-500 mx-auto" />
        <p className="m-0 mt-3 text-lg font-extrabold text-slate-900">Request received!</p>
        <p className="m-0 mt-1 text-sm text-slate-500">Our venue team will get in touch with you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-[0_10px_36px_-12px_rgba(15,23,42,0.14)]" data-testid="venue-booking-form">
      <div className="bg-[#0A0F2B] px-7 sm:px-9 py-6">
        <h3 className="m-0 text-xl sm:text-2xl font-extrabold text-white tracking-tight">Venue Booking Form</h3>
        <p className="m-0 mt-1 text-sm text-slate-300">Tell us what you need — our venue team will get in touch.</p>
      </div>
      <div className="p-7 sm:p-9 flex flex-col gap-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={lbl}>Name of Contact <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="venue-field-name" required />
          </div>
          <div>
            <label className={lbl}>Company Name <span className="text-rose-500">*</span></label>
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={fld} data-testid="venue-field-company" required />
          </div>
          <div>
            <label className={lbl}>Contact No. <span className="text-rose-500">*</span></label>
            <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} inputCls={fld} placeholder="10-digit mobile" testid="venue-field-phone" required />
          </div>
          <div>
            <label className={lbl}>Preferred Training Start Date <span className="text-rose-500">*</span></label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={fld} data-testid="venue-field-date" required />
          </div>
          <div>
            <label className={lbl}>Email <span className="text-rose-500">*</span></label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fld} data-testid="venue-field-email" required />
          </div>
          <div>
            <label className={lbl}>No. of Pax <span className="text-rose-500">*</span></label>
            <input type="number" min="1" value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} className={fld} data-testid="venue-field-pax" required />
          </div>
          <div>
            <label className={lbl}>Select Location <span className="text-rose-500">*</span></label>
            <select value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={fld} data-testid="venue-field-location" required>
              <option value="">Choose a centre</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Duration (No. of Days) <span className="text-rose-500">*</span></label>
            <input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} className={fld} data-testid="venue-field-days" required />
          </div>
        </div>
        <fieldset>
          <legend className={lbl}>Training Facilities Required</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6 mt-1">
            {FACILITIES.map((f) => (
              <label key={f} className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid={`venue-facility-${f.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <input type="checkbox" checked={facilities.includes(f)} onChange={() => toggleFacility(f)} className="w-4 h-4 rounded border-slate-300 accent-amber-500" />
                {f}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label className={lbl}>Any Remarks</label>
          <textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className={fld + " resize-y"} data-testid="venue-field-remarks" />
        </div>
        <ConsentCheckbox checked={consent} onChange={setConsent} testid="venue-consent" />
        <button type="submit" disabled={sending} className="self-center mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-500 text-[#0A0F2B] font-bold px-10 py-3.5 text-sm transition-colors disabled:opacity-50" data-testid="venue-form-submit">
          {sending ? "Submitting…" : "Get in Touch"}
        </button>
      </div>
    </form>
  );
}
